import styled from "styled-components";
import { BasicTable, Button, NoResults, StatePill, Text, Tooltip, Totalizers } from "@ficatranquilo/contabilidade.com-ui";
import { escapeHtml, formatHours } from "../ow";
import { WEEKDAY_LABELS, shortDate, formatPeriodLabel } from "../../helpers/timeClock";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || "20px"};
`;

const Inline = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const Help = styled.span`
  cursor: help;
`;

/** Saldo com sinal e sem decimal: 30 → "+0h30", -90 → "-1h30", 0 → "0h". */
export function formatSignedHours(minutes) {
  if (minutes === null || minutes === undefined) return "-";
  const rounded = Math.round(minutes);
  return `${rounded > 0 ? "+" : ""}${formatHours(rounded)}`;
}

/** Cor do saldo, como nos `Totalizers` das outras telas: positivo P800, negativo E400. */
export function balanceColor(minutes) {
  if (!minutes) return "N900";
  return minutes > 0 ? "P800" : "E400";
}

function Balance({ minutes }) {
  if (minutes === null || minutes === undefined) return <Text $text="-" $variant="paragraph_small_medium" $color="N400" />;
  return <Text $text={formatSignedHours(minutes)} $variant="paragraph_small_bold" $color={balanceColor(minutes)} />;
}

function Entries({ entries }) {
  if (!entries.length) return <Text $text="-" $variant="paragraph_small_medium" $color="N400" />;
  return (
    <Inline>
      {entries.map((entry, index) => {
        const separator = index < entries.length - 1 ? "," : "";
        if (entry.source !== "ADJUSTMENT") {
          return (
            <span key={entry.id}>
              {entry.time}
              {separator}
            </span>
          );
        }
        return (
          <Tooltip key={entry.id} content="Batida incluída por ajuste aprovado" $position="top" $size="medium" $color="dark">
            <Help>
              {entry.time}*{separator}
            </Help>
          </Tooltip>
        );
      })}
    </Inline>
  );
}

/**
 * Estado do dia em pílulas (a tabela da lib não pinta a linha), com o nome do feriado
 * ou da folga ao lado. Com `onRequestAdjustment`, o dia com batida ímpar (fora o que
 * está em andamento) ganha o "Solicitar ajuste".
 */
function DayNotes({ day, onRequestAdjustment }) {
  const notes = [];
  if (day.in_progress) notes.push(<StatePill key="progress" $text="Em andamento" $variant="blue" $size="small" />);
  if (day.inconsistent) notes.push(<StatePill key="odd" $text="Batida ímpar" $variant="red" $size="small" />);
  if (day.is_holiday) {
    notes.push(<StatePill key="holiday" $text="Feriado" $variant="gray" $size="small" />);
    if (day.holiday_name) notes.push(<Text key="holiday-name" $text={escapeHtml(day.holiday_name)} $variant="caption_medium" $color="N500" />);
  }
  if (day.is_day_off) {
    const label = day.day_off_hours ? `Folga de ${formatHours(day.day_off_hours * 60)}` : "Folga";
    notes.push(<StatePill key="day-off" $text={label} $variant="gray" $size="small" />);
    notes.push(
      <Text key="day-off-name" $text={escapeHtml(day.day_off_name || "Folga da empresa")} $variant="caption_medium" $color="N500" />
    );
  }
  if (onRequestAdjustment && day.inconsistent && !day.in_progress) {
    notes.push(
      <Button
        key="request"
        $text="Solicitar ajuste"
        $variant="outline"
        $size="small"
        $noWidth
        $action={() => onRequestAdjustment(day.date)}
      />
    );
  }
  return notes.length ? <Inline>{notes}</Inline> : null;
}

const columnsFor = (onRequestAdjustment) => [
  {
    id: "day",
    header: "Dia",
    size: 110,
    cell: ({ row }) => {
      const day = row.original;
      // Sem expediente (fim de semana, feriado, folga) em N400, no lugar da linha cinza de antes
      const color = day.is_workday ? "N900" : "N400";
      return (
        <Inline>
          <Text $text={shortDate(day.date)} $variant="paragraph_small_medium" $color={color} />
          <Text $text={WEEKDAY_LABELS[day.weekday]} $variant="caption_medium" $color="N400" />
        </Inline>
      );
    },
  },
  { id: "entries", header: "Batidas", size: 240, cell: ({ row }) => <Entries entries={row.original.entries || []} /> },
  {
    id: "worked",
    header: "Trabalhado",
    size: 110,
    align: "right",
    cell: ({ row }) => (row.original.is_future ? "-" : formatHours(row.original.worked_minutes)),
  },
  { id: "expected", header: "Esperado", size: 110, align: "right", cell: ({ row }) => formatHours(row.original.expected_minutes) },
  { id: "balance", header: "Saldo", size: 110, align: "right", cell: ({ row }) => <Balance minutes={row.original.balance_minutes} /> },
  {
    id: "notes",
    header: "Observações",
    size: 300,
    cell: ({ row }) => <DayNotes day={row.original} onRequestAdjustment={onRequestAdjustment} />,
  },
];

/** Totais do período e do banco de horas, na ordem da conta: anterior + período + acertos = final. */
function mirrorTotals(mirror) {
  const totals = mirror.totals || {};
  const bank = mirror.bank;
  // O backend passou a devolver period_* e mantém os month_* como alias durante o
  // rollout; ler os dois evita depender de qual versão respondeu.
  const periodBalance = bank ? (bank.period_balance_minutes ?? bank.month_balance_minutes) : totals.balance_minutes;
  const items = [
    { name: "Trabalhado", value: formatHours(totals.worked_minutes ?? 0), color: "N900" },
    { name: "Esperado", value: formatHours(totals.expected_minutes ?? 0), color: "N900" },
    { name: "Saldo do período", value: formatSignedHours(periodBalance ?? 0), color: balanceColor(periodBalance) },
  ];
  if (bank) {
    const events = bank.period_events_minutes ?? bank.month_events_minutes;
    items.push(
      { name: "Banco anterior", value: formatSignedHours(bank.previous_balance_minutes ?? 0), color: balanceColor(bank.previous_balance_minutes) },
      { name: "Acertos no período", value: formatSignedHours(events ?? 0), color: balanceColor(events) },
      { name: "Banco de horas final", value: formatSignedHours(bank.final_balance_minutes ?? 0), color: balanceColor(bank.final_balance_minutes) }
    );
  }
  return items;
}

/**
 * Espelho de ponto de um contrato CLT (empresa em /c/time-clock e colaborador em
 * /e/time-clock). Recebe o objeto `mirror` retornado por /time-clock-reports/mirror.
 *
 * O período pode ser um mês calendário (colaborador) ou o intervalo de fechamento da
 * empresa, por isso os rótulos falam em "período". Os totais contam só os dias
 * fechados: o dia atual fica em aberto.
 *
 * `showPeriod` escreve o intervalo acima dos totais: útil onde a tela não o mostra (a
 * competência do colaborador pode ser recortada pelo início do contrato) e dispensável
 * onde já há a linha do período acima.
 *
 * `onRequestAdjustment(date)` (só o colaborador) põe o "Solicitar ajuste" nos dias com
 * batida ímpar; recebe o dia em 'YYYY-MM-DD'.
 */
export default function TimeClockMirrorTable({ mirror, showPeriod = true, onRequestAdjustment }) {
  if (!mirror || !mirror.days) {
    return null;
  }

  return (
    <Stack>
      {showPeriod && mirror.period ? (
        <Text
          $text="Período: %s"
          $replaces={[formatPeriodLabel(mirror.period.start_date, mirror.period.end_date)]}
          $variant="paragraph_small_medium"
          $color="N500"
        />
      ) : null}

      <Totalizers totals={mirrorTotals(mirror)} />

      <Stack $gap="10px">
        <BasicTable
          data={mirror.days}
          columns={columnsFor(onRequestAdjustment)}
          getRowId={(day) => day.date}
          noResults={<NoResults notResultText="Nenhum dia neste período" />}
        />
        <Text
          $text="* Batida incluída por ajuste aprovado. O saldo do dia aplica a tolerância de 10 minutos (art. 58 da CLT). O dia atual fica em aberto e só entra nos totais depois de encerrado."
          $variant="caption_medium"
          $color="N400"
        />
      </Stack>
    </Stack>
  );
}
